import { useRef, useState, type RefObject } from "react";
import DownloadIconButton from "@/components/DownloadIconButton";
import MyDialogContent from "@/components/MyDialogContent";
import { Dialog } from "@/components/ui/dialog";
import { formatDateTime } from "@/utils/helpers";
import { toast } from "sonner";
import type { PatientV2 } from "../types";
import { downloadPatientHistoryPdf } from "./downloadPatientHistoryPdf";
import {
  firstDefinedPatientValue,
  firstNonEmptyPatientString,
  formatPersonRef,
} from "./patientHistoryHelpers";

type Props = {
  patient: PatientV2 | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function dash(value: unknown): string {
  return value && String(value).trim() ? String(value).trim() : "—";
}

function PatientEnrollmentHistoryContent({ patient }: { patient: PatientV2 | null }) {
  const record = (patient ?? {}) as Record<string, unknown>;
  const patientCreatedRaw = firstDefinedPatientValue(record, ["createdAt", "_creationTime"]);
  const patientCreatedDisplay = patientCreatedRaw ? formatDateTime(patientCreatedRaw as string | number | Date | null | undefined) : "";
  const enrolledRaw = firstDefinedPatientValue(record, [
    "enrolledDate",
    "enrolledAt",
    "enrollmentDate",
    "enrollmentDateTime",
  ]);
  const enrolledDisplay = enrolledRaw ? formatDateTime(enrolledRaw as string | number | Date | null | undefined) : "";
  const enrolledByDisplay =
    formatPersonRef(record.enrolledBy) ||
    formatPersonRef(record.enrolledByUser) ||
    (typeof record.enrolledByName === "string" && record.enrolledByName.trim()
      ? record.enrolledByName.trim()
      : "");
  const enrollmentNoteText = firstNonEmptyPatientString(record, [
    "enrollmentNote",
    "enrollNote",
    "notesOnEnrollment",
    "patientEnrollmentNote",
    "enrollmentNotes",
  ]);

  return (
    <div className="space-y-5 text-sm">
      <div className="space-y-4">
        <div className="border-l-2 border-primary pl-3">
          <p className="font-medium text-foreground">Patient creation</p>
          <p className="text-muted-foreground mt-0.5 text-sm">{dash(patientCreatedDisplay)}</p>
        </div>
        {patient?.status !== "pending" ? (
          <>
            <div className="border-l-2 border-primary pl-3">
              <p className="font-medium text-foreground">Enrolled on</p>
              <p className="text-muted-foreground mt-0.5 text-sm">{dash(enrolledDisplay)}</p>
            </div>
            <div className="border-l-2 border-primary pl-3">
              <p className="font-medium text-foreground">Enrolled by</p>
              <p className="text-muted-foreground mt-0.5 text-sm">{dash(enrolledByDisplay)}</p>
            </div>
            <div className="border-l-2 border-primary pl-3">
              <p className="font-medium text-foreground">Enrollment note</p>
              <p className="text-muted-foreground mt-1 whitespace-pre-wrap text-sm leading-relaxed">
                {dash(enrollmentNoteText)}
              </p>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function PatientHistoryPdfTemplate({
  patient,
  pdfRef,
}: {
  patient: PatientV2 | null;
  pdfRef: RefObject<HTMLDivElement | null>;
}) {
  const record = (patient ?? {}) as Record<string, unknown>;
  const fullName =
    `${patient?.firstName || ""} ${patient?.middleName || ""} ${patient?.lastName || ""}`.trim() ||
    "—";
  const enrolledRaw = firstDefinedPatientValue(record, [
    "enrolledDate",
    "enrolledAt",
    "enrollmentDate",
    "enrollmentDateTime",
  ]);
  const enrolledDisplay = enrolledRaw ? formatDateTime(enrolledRaw as string | number | Date | null | undefined) : "—";
  const consentProof = firstNonEmptyPatientString(record, [
    "zendeskTicketNumber",
    "consentProof",
    "consentProofId",
    "consentProofRef",
    "consentTicketNumber",
  ]);

  return (
    <div
      ref={pdfRef}
      style={{
        position: "fixed",
        top: "-9999px",
        left: "-9999px",
        width: 595,
        backgroundColor: "#ffffff",
        color: "#000000",
        padding: "12px 18px",
        fontFamily: "'Segoe UI', Arial, sans-serif",
        fontSize: 12,
        lineHeight: 1.6,
        pointerEvents: "none",
      }}
      aria-hidden
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <img
          src="/logo.png"
          alt="Logo"
          crossOrigin="anonymous"
          style={{ height: 36, width: "auto", objectFit: "contain" }}
        />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Patient History</div>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Enrollment Details</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "170px 1fr",
            rowGap: 6,
            columnGap: 10,
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600 }}>Patient Name</div>
          <div>{fullName}</div>
          <div style={{ fontWeight: 600 }}>Enrolled - Date &amp; Time</div>
          <div>{enrolledDisplay}</div>
          <div style={{ fontWeight: 600 }}>Consent proof</div>
          <div>{consentProof || "—"}</div>
          <div style={{ fontWeight: 600 }}>Consent checkbox</div>
          <div>
            <div style={{ fontWeight: 600 }}>Checked</div>
            <div style={{ marginTop: 4 }}>
              I hereby provide my informed consent to enroll in care management services offered by
              Anthro Care On behalf of my primary care provider. I understand that these services
              involve the collection and monitoring of my health data, ongoing care coordination, and
              communication with healthcare providers to support the management of my condition(s). I
              acknowledge that participation is voluntary, and I may opt out at any time by
              informing the care team. I authorize the use and disclosure of my health information
              as necessary to deliver these services in accordance with applicable laws and
              regulations.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PatientEnrollmentHistoryDialog({ patient, open, onOpenChange }: Props) {
  const pdfRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    const element = pdfRef.current;
    if (!element) {
      toast.error("PDF template is not ready. Please try again.");
      return;
    }

    setIsDownloading(true);
    try {
      await downloadPatientHistoryPdf(element, patient);
      toast.success("Patient history PDF downloaded");
    } catch (error) {
      console.error("Patient history PDF generation error:", error);
      toast.error("Failed to download patient history PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <MyDialogContent heading="Patient history">
          {patient?.status !== "pending" ? (
            <DownloadIconButton
              onClick={() => void handleDownload()}
              disabled={isDownloading}
              tooltip="Download PDF"
              ariaLabel="Download patient history PDF"
              className="absolute right-12 top-2"
            />
          ) : null}
          <PatientEnrollmentHistoryContent patient={patient} />
        </MyDialogContent>
      </Dialog>
      <PatientHistoryPdfTemplate patient={patient} pdfRef={pdfRef} />
    </>
  );
}
